import Image from "next/image";
import Link from "next/link";

import { cn } from "@/lib/utils";

import type { ReactNode } from "react";

type WidgetCardProps = {
  children: ReactNode;
  className?: string;
};

export function WidgetCard({ children, className }: WidgetCardProps) {
  return (
    <div
      className={cn(
        "bg-white rounded-2xl shadow-2xl w-full max-w-xl h-fit overflow-hidden p-10 flex flex-col gap-12",
        className
      )}
    >
      {children}
    </div>
  );
}

type WidgetHeaderProps = {
  title: string;
  className?: string;
};

export function WidgetHeader({ title, className }: WidgetHeaderProps) {
  return (
    <h2 className={cn("text-2xl font-semibold font-clash uppercase -tracking-[1%] text-rebolt-500", className)}>
      {title}
    </h2>
  );
}

type WidgetContentProps = {
  children: ReactNode;
  className?: string;
};

export function WidgetContent({ children, className }: WidgetContentProps) {
  return <div className={cn("flex flex-col gap-6", className)}>{children}</div>;
}

type WidgetBodyProps = {
  children: ReactNode;
  className?: string;
};

export function WidgetBody({ children, className }: WidgetBodyProps) {
  return <p className={cn("font-medium text-3xl font-clash -tracking-[1%]", className)}>{children}</p>;
}

type WidgetFooterProps = {
  children: ReactNode;
  className?: string;
};

export function WidgetFooter({ children, className }: WidgetFooterProps) {
  return <div className={cn(className)}>{children}</div>;
}

type StatisticProps = {
  title?: string;
  body: string;
  source: string;
};

export function Statistic({ title = "Did you know?", body, source }: StatisticProps) {
  return (
    <WidgetCard>
      <WidgetContent>
        <WidgetHeader title={title} />
        <WidgetBody>{body}</WidgetBody>
      </WidgetContent>
      <WidgetFooter>
        <p className="font-medium text-ash-600">Source: {source}</p>
      </WidgetFooter>
    </WidgetCard>
  );
}

type TestimonialProps = {
  title?: string;
  quote: string;
  author: {
    name: string;
    company: string;
    image: string;
  };
};

export function ReboltTestimonial({ title = "Did you know?", quote, author }: TestimonialProps) {
  return (
    <WidgetCard>
      <WidgetContent>
        <WidgetHeader title={title} />
        <WidgetBody>{quote}</WidgetBody>
      </WidgetContent>
      <WidgetFooter className="flex gap-6 items-center">
        <div className="w-20 h-20 rounded-full overflow-hidden relative">
          <Image src={author.image} alt={author.name} width={80} height={80} className="w-full h-full object-cover" />
        </div>
        <div className="flex flex-col">
          <p className="font-semibold text-ash-800 text-2xl font-clash">{author.name}</p>
          <p className="font-medium text-ash-600 text-xl">{author.company}</p>
        </div>
      </WidgetFooter>
    </WidgetCard>
  );
}

type ReboltMessageProps = {
  title?: string;
  body: string;
};

export function ReboltMessage({ title = "Did you know?", body }: ReboltMessageProps) {
  return (
    <WidgetCard>
      <WidgetContent>
        <WidgetHeader title={title} />
        <WidgetBody>{body}</WidgetBody>
      </WidgetContent>
    </WidgetCard>
  );
}

type AppStoreReviewProps = {
  title?: string;
  quote: string;
  date: string;
};

export function AppStoreReview({ title = "Did you know?", quote, date }: AppStoreReviewProps) {
  return (
    <WidgetCard>
      <WidgetContent>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star key={star} className="w-6 h-6" />
          ))}
        </div>
        <WidgetHeader title={title} />
        <WidgetBody>{quote}</WidgetBody>
      </WidgetContent>
      <WidgetFooter className="flex gap-6 items-center">
        <div className="flex flex-col">
          <p className="font-semibold text-ash-800 text-2xl font-clash">App Store Review</p>
          <p className="font-medium text-ash-600 text-xl">{date}</p>
        </div>
      </WidgetFooter>
    </WidgetCard>
  );
}

export function Star({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      className={className}
    >
      <path
        d="M10.5268 1.29489C10.5706 1.20635 10.6383 1.13183 10.7223 1.07972C10.8062 1.02761 10.903 1 11.0018 1C11.1006 1 11.1974 1.02761 11.2813 1.07972C11.3653 1.13183 11.433 1.20635 11.4768 1.29489L13.7868 5.97389C13.939 6.28186 14.1636 6.5483 14.4414 6.75035C14.7192 6.95239 15.0419 7.08401 15.3818 7.13389L20.5478 7.88989C20.6457 7.90408 20.7376 7.94537 20.8133 8.00909C20.8889 8.07282 20.9452 8.15644 20.9758 8.2505C21.0064 8.34456 21.0101 8.4453 20.9864 8.54133C20.9627 8.63736 20.9126 8.72485 20.8418 8.79389L17.1058 12.4319C16.8594 12.672 16.6751 12.9684 16.5686 13.2955C16.4622 13.6227 16.4369 13.9708 16.4948 14.3099L17.3768 19.4499C17.3941 19.5477 17.3835 19.6485 17.3463 19.7406C17.3091 19.8327 17.2467 19.9125 17.1663 19.9709C17.086 20.0293 16.9908 20.0639 16.8917 20.0708C16.7926 20.0777 16.6935 20.0566 16.6058 20.0099L11.9878 17.5819C11.6835 17.4221 11.345 17.3386 11.0013 17.3386C10.6576 17.3386 10.3191 17.4221 10.0148 17.5819L5.3978 20.0099C5.31013 20.0563 5.2112 20.0772 5.11225 20.0701C5.0133 20.0631 4.91832 20.0285 4.83809 19.9701C4.75787 19.9118 4.69563 19.8321 4.65846 19.7401C4.62128 19.6481 4.61066 19.5476 4.6278 19.4499L5.5088 14.3109C5.567 13.9716 5.54178 13.6233 5.43534 13.2959C5.32889 12.9686 5.14441 12.672 4.8978 12.4319L1.1618 8.79489C1.09039 8.72593 1.03979 8.63829 1.01576 8.54197C0.991731 8.44565 0.995237 8.34451 1.02588 8.25008C1.05652 8.15566 1.11307 8.07174 1.18908 8.00788C1.26509 7.94402 1.3575 7.90279 1.4558 7.88889L6.6208 7.13389C6.96106 7.08439 7.28419 6.95295 7.56238 6.75088C7.84058 6.54881 8.0655 6.28216 8.2178 5.97389L10.5268 1.29489Z"
        fill="#FF5700"
        stroke="#FF5700"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ReboltAppCTA({
  title = "Get the app",
  description = "Download the Rebolt app and unlock an extra week in your trial! Take your Rebolt tools with you, wherever you go.",
  backgroundImage = "/onboarding/app-cta.png",
}: {
  title?: string;
  description?: string;
  backgroundImage?: string;
}) {
  return (
    <div
      className="flex flex-col gap-6 p-9 justify-between bg-cover bg-center bg-no-repeat rounded-2xl min-h-[650px] max-w-xl w-full shadow-2xl"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <div className="flex flex-col gap-4">
        <h2 className="xl:text-5xl text-3xl font-semibold font-clash -tracking-[1%] text-white">{title}</h2>
        <p className="xl:text-2xl text-base text-white font-semibold -tracking-[1%]">{description}</p>
      </div>
      <div className="flex gap-4 flex-wrap">
        <Link href="https://rebolthq.com/ios" target="_blank" rel="noopener noreferrer">
          <Image
            src="/ios-app-store.png"
            alt="Download on the App Store"
            width={140}
            height={42}
            className="h-10 sm:h-12 w-auto"
          />
        </Link>
        <Link href="https://rebolthq.com/android" target="_blank" rel="noopener noreferrer">
          <Image
            src="/google-play.png"
            alt="Get it on Google Play"
            width={140}
            height={42}
            className="h-10 sm:h-12 w-auto"
          />
        </Link>
      </div>
    </div>
  );
}
